import { useCallback, useEffect, useState } from 'react';
import { api, errorText } from '../api';
import { Badge, Card, Loading, Notice, TextField } from '../components/ui';

interface Modulzeile {
  schluessel: string;
  name: string;
  beschreibung: string;
  kern: boolean;
  pfade: string[];
  gebucht: boolean;
  gebucht_bis: string | null;
  standorte: number;
  aktiv: boolean;
  notiz: string | null;
  gueltig: boolean;
  laeuft_ab_in: number | null;
}

interface Betriebsart {
  name: string;
  beschreibung: string;
  module: string[];
}

interface Stand {
  betriebsart: 'logistik' | 'handel' | 'eigenlager' | 'individuell';
  module: Modulzeile[];
  abgelaufen: string[];
  betriebsarten: Record<string, Betriebsart>;
}

interface LizenzStand {
  aktiv: boolean;
  quelle: 'datei' | 'db' | null;
  fehler: string | null;
  lizenz: {
    kunde: string;
    ausgestellt: string;
    betriebsart?: string;
    module: { schluessel: string; gebuchtBis: string | null }[];
  } | null;
}

export function ModulesPage() {
  const [stand, setStand] = useState<Stand | null>(null);
  const [betreiberSchluessel, setBetreiberSchluessel] = useState('');
  const [entsperrt, setEntsperrt] = useState(false);
  const [bis, setBis] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [lizenz, setLizenz] = useState<LizenzStand | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      setStand(await api<Stand>('/api/system/module'));
      setLizenz(await api<LizenzStand>('/api/system/lizenz'));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  async function lizenzHochladen(datei: File) {
    setError('');
    setMessage('');
    setBusy(true);
    try {
      const umschlag = JSON.parse(await datei.text());
      await api('/api/system/lizenz', { method: 'POST', body: umschlag });
      setMessage('Lizenz übernommen.');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function lizenzEntfernen() {
    setBusy(true);
    setError('');
    try {
      await api('/api/system/lizenz', { method: 'DELETE' });
      setMessage('Lizenz entfernt.');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, [load]);

  async function schalte(schluessel: string, aktiv: boolean) {
    setBusy(true);
    setError('');
    try {
      setStand(
        await api<Stand>('/api/system/module/aktiv', {
          method: 'PUT',
          body: { schluessel, aktiv },
        }),
      );
      setMessage('Menü angepasst. Nach dem nächsten Laden der Seite sichtbar.');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function setzeBetriebsart(betriebsart: string) {
    setBusy(true);
    setError('');
    try {
      setStand(
        await api<Stand>('/api/system/module/betriebsart', {
          method: 'PUT',
          body: { betriebsart },
        }),
      );
      setMessage('Betriebsart gesetzt und die Schalter danach vorbelegt.');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function buche(zeile: Modulzeile, gebucht: boolean) {
    setBusy(true);
    setError('');
    try {
      setStand(
        await api<Stand>('/api/system/module/buchung', {
          method: 'PUT',
          headers: { 'X-Betreiber-Schluessel': betreiberSchluessel },
          body: {
            schluessel: zeile.schluessel,
            gebucht,
            gebuchtBis: bis[zeile.schluessel]?.trim() || null,
          },
        }),
      );
      setEntsperrt(true);
      setMessage(`${zeile.name} ${gebucht ? 'gebucht' : 'abbestellt'}.`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!stand) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  const bald = stand.module.filter(
    (m) => m.laeuft_ab_in !== null && m.laeuft_ab_in >= 0 && m.laeuft_ab_in <= 30,
  );

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {stand.abgelaufen.length > 0 && (
        <Notice kind="error">
          <strong>Abgelaufen: {stand.abgelaufen.join(', ')}.</strong> Der Betrieb läuft weiter —
          es lassen sich nur keine neuen Daten in diesen Modulen anlegen. Vorhandene bleiben
          vollständig lesbar.
        </Notice>
      )}
      {bald.length > 0 && (
        <Notice kind="info">
          {bald.map((m) => `${m.name} läuft in ${m.laeuft_ab_in} Tagen ab`).join(' · ')}
        </Notice>
      )}

      <Card title="Lizenz (On-Premise)">
        {lizenz?.aktiv && lizenz.lizenz ? (
          <>
            <div className="small">
              Aktive Lizenz für <strong>{lizenz.lizenz.kunde}</strong> ({lizenz.quelle === 'datei' ? 'Datei' : 'hochgeladen'}
              , ausgestellt {lizenz.lizenz.ausgestellt}). Die Freischaltung unten wird durch diese Lizenz bestimmt.
            </div>
            <div className="small muted" style={{ marginTop: 6 }}>
              Module: {lizenz.lizenz.module.map((m) => m.schluessel + (m.gebuchtBis ? ` (bis ${m.gebuchtBis})` : '')).join(', ')}
            </div>
          </>
        ) : lizenz?.aktiv && lizenz.fehler ? (
          <Notice kind="error">Lizenz vorhanden, aber ungültig: {lizenz.fehler}</Notice>
        ) : (
          <div className="small muted">
            Keine Lizenz hochgeladen – die Freischaltung kommt aus der Buchung unten (Betreiberschlüssel). Für
            On-Premise-Instanzen hier die signierte <code>.lic</code>-Datei hochladen.
          </div>
        )}
        <div className="toolbar" style={{ marginTop: 12 }}>
          <label className="field" style={{ margin: 0 }}>
            <span>Lizenzdatei (.lic) hochladen</span>
            <input
              type="file"
              accept=".lic,application/json"
              disabled={busy || lizenz?.quelle === 'datei'}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void lizenzHochladen(f);
                e.target.value = '';
              }}
            />
          </label>
          <span className="spacer" />
          {lizenz?.quelle === 'db' && (
            <button onClick={() => void lizenzEntfernen()} disabled={busy}>
              Lizenz entfernen
            </button>
          )}
        </div>
        {lizenz?.quelle === 'datei' && (
          <div className="small muted">Lizenz kommt aus einer Serverdatei (LIZENZ_DATEI) und wird hier nicht überschrieben.</div>
        )}
      </Card>

      <Card title="Betriebsart">
        <div className="small muted" style={{ marginBottom: 14 }}>
          Belegt die Schalter darunter einmal sinnvoll vor — mehr nicht. Danach entscheidet der
          einzelne Schalter, und die Betriebsart steht auf <em>individuell</em>, sobald Sie
          abweichen.
        </div>
        <div className="grid kpi">
          {Object.entries(stand.betriebsarten).map(([schluessel, art]) => (
            <div
              key={schluessel}
              className={stand.betriebsart === schluessel ? 'kpi-tile alert' : 'kpi-tile'}
            >
              <div className="label">{art.name}</div>
              <div className="hint" style={{ minHeight: '2.6em' }}>
                {art.beschreibung}
              </div>
              <button
                style={{ marginTop: 8 }}
                onClick={() => setzeBetriebsart(schluessel)}
                disabled={busy || stand.betriebsart === schluessel}
              >
                {stand.betriebsart === schluessel ? 'eingestellt' : 'übernehmen'}
              </button>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Module" padded={false}>
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          <strong>Gebucht</strong> entscheidet der Betreiber, <strong>sichtbar</strong> Sie. Was
          nicht gebucht ist, lässt sich nicht mehr bebuchen — vorhandene Daten bleiben aber
          lesbar. Ein abbestelltes Modul versteckt nichts, es hört nur auf, Neues anzunehmen.
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Modul</th>
                <th>Gebucht</th>
                <th>Sichtbar</th>
                {entsperrt && <th>Laufzeit</th>}
              </tr>
            </thead>
            <tbody>
              {stand.module.map((m) => (
                <tr key={m.schluessel}>
                  <td>
                    <strong>{m.name}</strong>
                    {m.kern && (
                      <Badge kind="info">
                        <span style={{ marginLeft: 4 }}>Kern</span>
                      </Badge>
                    )}
                    <div className="small muted" style={{ maxWidth: '52ch' }}>
                      {m.beschreibung}
                    </div>
                  </td>
                  <td>
                    {m.kern ? (
                      <span className="muted small">immer</span>
                    ) : (
                      <>
                        <Badge kind={m.gueltig ? 'ok' : m.gebucht ? 'bad' : 'default'}>
                          {m.gebucht ? (m.gueltig ? 'gebucht' : 'abgelaufen') : 'nicht gebucht'}
                        </Badge>
                        {m.gebucht_bis && (
                          <div className="small muted">bis {m.gebucht_bis}</div>
                        )}
                        {entsperrt && (
                          <button
                            className="small"
                            style={{ marginTop: 6 }}
                            onClick={() => buche(m, !m.gebucht)}
                            disabled={busy}
                          >
                            {m.gebucht ? 'abbestellen' : 'buchen'}
                          </button>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    {m.kern ? (
                      <span className="muted small">immer</span>
                    ) : (
                      <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={m.aktiv}
                          disabled={busy || !m.gueltig}
                          onChange={(e) => schalte(m.schluessel, e.target.checked)}
                        />
                        <span className="small">{m.aktiv ? 'im Menü' : 'ausgeblendet'}</span>
                      </label>
                    )}
                  </td>
                  {entsperrt && (
                    <td>
                      {!m.kern && (
                        <input
                          value={bis[m.schluessel] ?? m.gebucht_bis ?? ''}
                          onChange={(e) =>
                            setBis((alt) => ({ ...alt, [m.schluessel]: e.target.value }))
                          }
                          placeholder="unbefristet"
                          style={{ width: 120 }}
                        />
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Buchung ändern">
        <div className="small muted" style={{ marginBottom: 12 }}>
          Was gebucht ist, entscheidet der Betreiber. Der Schlüssel dafür steht in der
          Serverkonfiguration und ist von hier aus nicht einsehbar — ohne ihn bleiben die
          Buchungsspalten gesperrt. Ist auf dem Server kein Schlüssel hinterlegt, ist die
          Funktion vollständig abgeschaltet.
        </div>
        <div className="form-grid">
          <TextField
            label="Betreiberschlüssel"
            type="password"
            value={betreiberSchluessel}
            onChange={setBetreiberSchluessel}
          />
        </div>
        <button
          onClick={() => setEntsperrt(betreiberSchluessel.length > 0)}
          disabled={betreiberSchluessel.length === 0}
        >
          Buchungsspalten anzeigen
        </button>
        {entsperrt && (
          <div className="small muted" style={{ marginTop: 10 }}>
            Der Schlüssel wird bei jeder Änderung mitgeschickt und nirgends gespeichert. Ist er
            falsch, weist der Server die Änderung ab.
          </div>
        )}
      </Card>
    </>
  );
}
